import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { OWN_LOGIN_EMAIL_REFUSAL } from "./apolloErrorReporting";
import { Auth } from "aws-amplify";
import { RedirectUri } from "./components/enumStrings";
import { setContext } from "@apollo/client/link/context";
import jwt_decode from "jwt-decode";
import * as Sentry from "@sentry/react";
import { ErrorCodeEnum } from "./graphql/types";
import {
  getApolloErrorConsoleSummary,
  getApolloOperationReportContext,
  isApolloErrorHandledLocally,
} from "./apolloErrorReporting";

const uri =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_PROD_API_URL
    : process.env.REACT_APP_DEV_API_URL;

const getNewAccessToken = async (): Promise<string | undefined> => {
  try {
    await Auth.currentSession();
    const token = await Auth.currentAuthenticatedUser(); // add method to get token here
    return token.signInUserSession.accessToken.jwtToken;
  } catch (e) {
    console.log("Error getting access token for apollo client:", e);
  }

  return undefined;
};

const getApolloClient = async (
  handleLogout: () => void
): Promise<ApolloClient<NormalizedCacheObject>> => {
  const errorLink = onError((e) => {
    const urlPath = window.location.pathname;
    const operationName = e.operation.operationName;
    console.error(
      "Caught Apollo Client Error",
      getApolloErrorConsoleSummary({
        graphQLErrorCount: e.graphQLErrors?.length || 0,
        hasNetworkError: Boolean(e.networkError),
        operationName,
      })
    );

    Sentry.withScope((scope) => {
      const { operation, graphQLErrors, networkError } = e;
      scope.setTransactionName(operationName);
      scope.setContext(
        "apolloGraphQLOperation",
        getApolloOperationReportContext({
          operationName,
          variables: operation.variables,
        })
      );

      graphQLErrors?.forEach((error) => {
        Sentry.captureMessage(error.message, {
          level: "error",
          fingerprint: ["{{ default }}", "{{ transaction }}"],
          contexts: {
            apolloGraphQLError: {
              message: error.message,
              extensions: error.extensions,
            },
          },
        });
      });

      if (networkError) {
        Sentry.captureMessage(networkError.message, {
          level: "error",
          contexts: {
            apolloNetworkError: {
              message: networkError.message,
            },
          },
        });
      }
    });

    // if blacklist ting
    if (
      (e?.graphQLErrors || [{ message: "" }])[0]?.message.includes(
        ErrorCodeEnum.DisallowAccessTokenWithUserAuthYoungerThanUtcMilli
      ) ||
      (e?.graphQLErrors || [{ message: "" }])[0]?.message.includes(
        ErrorCodeEnum.DisallowAccessTokenPermanently
      ) ||
      (e?.graphQLErrors || [{ message: "" }])[0]?.message.includes(
        ErrorCodeEnum.DisallowUserTypeAccess
      )
    ) {
      if (urlPath !== RedirectUri.logout) {
        handleLogout();
        return;
      }
    }

    // The calling screen shows these errors and must stay open.
    if (isApolloErrorHandledLocally(operationName)) {
      return;
    }

    // A member cannot change the email of their own login; the profile page
    // shows this message and stays open.
    if (
      (e?.graphQLErrors || [])[0]?.message?.startsWith(OWN_LOGIN_EMAIL_REFUSAL)
    ) {
      return;
    }

    if (
      urlPath !== RedirectUri.errorGeneral &&
      urlPath !== RedirectUri.support // ignoring this to prevent checking for graphql errors on the support page
    ) {
      window.location.href = RedirectUri.errorGeneral;
    }
  });

  let accessToken = await getNewAccessToken();

  const authLink = setContext(async (operation, previousContext) => {
    // refresh token if it's expired bruv and not a guest
    if (
      accessToken &&
      // eslint-disable-next-line
      // @ts-ignore
      Date.now() >= (jwt_decode(accessToken).exp as number) * 1000
    ) {
      accessToken = await getNewAccessToken();
    }

    let apiAuthorization = accessToken ? `Bearer ${accessToken}` : "";

    // attach access token for the manual requests made during setting a permanent password while using a temporary password
    if (
      previousContext &&
      previousContext?.customAccessTokenForSettingHasSignedUp &&
      previousContext?.customAccessTokenForSettingHasSignedUp?.length > 0
    ) {
      apiAuthorization = `Bearer ${previousContext.customAccessTokenForSettingHasSignedUp}`;
    }

    return {
      ...previousContext, // ensure previous context is passed along to sustain cache and all the other apollo graphql goodies (without passing this everything breaks)
      headers: {
        authorization: apiAuthorization,
      },
    };
  });

  const httpLink = new HttpLink({
    uri,
  });

  const link = ApolloLink.from([errorLink, authLink, httpLink]);

  const client = new ApolloClient({
    link,
    cache: new InMemoryCache(),
  });

  return client;
};

export default getApolloClient;
